import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useHistory } from "react-router-dom";
import { Button as ShadcnButton } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { TradeFilterSelectFullWidth } from "../trade-filter-selects";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { toastWarning } from "../../utils/showApiErrorToast";
import { toast } from "sonner";
import { PineScriptCodeBlock } from "../Reports/PineScriptCodeBlock";
import { useReportStrategyPineRequirements } from "../../hooks/useReportStrategyPineRequirements";
import { composeReportStrategyPolicy, updateReportStrategy } from "../../services/reportsApi";
import type { ReportStrategyPolicyVersion, ReportStrategyResponse } from "../../types/reports";
import type {
  ComposeOutputMode,
  ComposeReportStrategyPolicyResponse,
  ReportStrategyPineParseResult,
} from "../../types/reportStrategyPine";
import {
  APPEND_POLICY_TO_INDICATOR_USER_COPY,
  coerceUxCopy,
  getEffectiveInjectReadiness,
  getEffectiveParseIssues,
  getPineIr,
  getPineScriptKindDisplay,
  getRequirementsWarningCatalog,
  isIndicatorPineScript,
  normalizeComposeWarnings,
} from "../../utils/pineIrUi";
import { StrategyComposeNoPolicyPrompt } from "./StrategyComposeNoPolicyPrompt";
import {
  canMergePolicyIntoStrategy,
  formatComposeModeLabel,
  isPolicyBundleVersion,
  mergeDisabledReason,
  pickDefaultComposeMode,
  storeComposeMode,
  versionOptionLabel,
} from "../../utils/reportStrategyPineUi";
import { mergeStrategyWithParseSummary } from "../../utils/strategyComposeUi";
import { StrategyComposeInjectionPreviewCard } from "./StrategyComposeInjectionPreviewCard";
import { StrategyComposeModePicker } from "./StrategyComposeModePicker";
import { StrategyPineExecutionModelCallout } from "./StrategyPineExecutionModelCallout";
import {
  COMPOSE_EDUCATION_STEP_DESCRIPTION,
  COMPOSE_EDUCATION_STEP_TITLE,
  ComposeEducationFooter,
  StrategyPineComposeEducationStep,
  useStrategyPineEducationGate,
} from "./StrategyPineComposeEducation";
import { StrategyPineParseIssues } from "./StrategyPineParseIssues";
import { StrategyPineParseSummaryAlert } from "./StrategyPineParseSummaryAlert";
import {
  StrategyPineDetailDialog,
  type StrategyPineDetailDialogSize,
} from "./StrategyPineDetailDialog";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "./StrategyPineSectionCard";

export type StrategyComposeModalShell = "chakra" | "pine-detail" | "embedded";

export type ComposeFlowStep = "form" | "education";

export type EmbeddedComposePresentation = {
  title: string;
  description?: React.ReactNode;
  footer: React.ReactNode | null;
  size: StrategyPineDetailDialogSize;
};

export type StrategyComposeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  strategy: ReportStrategyResponse;
  versions: ReportStrategyPolicyVersion[];
  currentPolicyVersionId: string | null;
  preferredPolicyVersionId?: string | null;
  subtleText: string;
  textColor: string;
  borderColor: string;
  onStrategyRefresh: () => void | Promise<void>;
  parseResult?: ReportStrategyPineParseResult | null;
  onComposeSuccess?: (composedPolicyVersionId: string) => void | Promise<void>;
  /** Fires when the user confirms compose (before the API request). */
  onComposeStart?: () => void;
  /** Fires when compose fails after onComposeStart. */
  onComposeFailed?: () => void;
  /** chakra = legacy modal; pine-detail = standalone shadcn dialog; embedded = body only inside parent dialog */
  shell?: StrategyComposeModalShell;
  /** When shell is embedded, supplies the footer primary action for the parent dialog */
  onComposeFooterChange?: (footer: React.ReactNode | null) => void;
  /** When shell is embedded, updates parent StrategyPineDetailDialog chrome per flow step. */
  onEmbeddedPresentationChange?: (presentation: EmbeddedComposePresentation | null) => void;
  /** Switch strategy detail to Policy versions tab (e.g. combine policies). */
  onNavigateToPoliciesTab?: () => void;
  /** Secondary dismiss — reverts pending policy switches without composing. */
  onCancel?: () => void;
};

export const COMPOSE_DIALOG_DESCRIPTION =
  "Attach a saved policy version to your master Pinescript and generate the script to paste into TradingView.";

/** Shared max width for Compose for TradingView dialogs (form + education steps). */
export const STRATEGY_COMPOSE_DIALOG_CONTENT_CLASS = "sm:max-w-[640px]";

export function StrategyComposeModal({
  isOpen,
  onClose,
  strategy,
  versions,
  currentPolicyVersionId,
  preferredPolicyVersionId,
  subtleText,
  textColor,
  borderColor,
  onStrategyRefresh,
  parseResult,
  onComposeSuccess,
  onComposeStart,
  onComposeFailed,
  shell = "chakra",
  onComposeFooterChange,
  onEmbeddedPresentationChange,
  onNavigateToPoliciesTab,
  onCancel,
}: StrategyComposeModalProps): React.ReactElement | null {
  const history = useHistory();
  const [flowStep, setFlowStep] = useState<ComposeFlowStep>("form");
  const { data: requirements } = useReportStrategyPineRequirements();
  const strategyMerged = useMemo(() => mergeStrategyWithParseSummary(strategy), [strategy]);

  const injectReadiness = getEffectiveInjectReadiness(strategyMerged, parseResult);
  const executionModel = getPineIr(parseResult)?.execution_model;
  const warningCatalog = getRequirementsWarningCatalog(requirements);
  const parseIssues = useMemo(() => {
    if (parseResult) {
      return getEffectiveParseIssues(strategyMerged, parseResult, warningCatalog);
    }
    return strategy.pine_parse_summary?.parse_issues_top ?? [];
  }, [parseResult, strategy.pine_parse_summary?.parse_issues_top, strategyMerged, warningCatalog]);

  const mergeAllowed = canMergePolicyIntoStrategy(strategyMerged, parseResult);
  const mergeReason = mergeDisabledReason(strategyMerged, parseResult);
  const isIndicatorScript = isIndicatorPineScript(strategyMerged, parseResult);

  const composeSummaryFields = useMemo(
    () => ({
      ...strategyMerged,
      inject_readiness: injectReadiness as typeof strategy.inject_readiness,
      exit_hook_readiness:
        parseResult?.exit_hook_readiness ??
        parseResult?.pine_ir?.exit_hook_readiness ??
        strategy.exit_hook_readiness,
    }),
    [injectReadiness, parseResult, strategy.exit_hook_readiness, strategy.inject_readiness, strategyMerged]
  );

  // Embedded compose follows the Active TV checklist; skip duplicate all-clear banners.
  const showReadinessSummary = useMemo(() => {
    if (!composeSummaryFields.pine_parse_status) return false;
    if (shell !== "embedded") return true;
    const allClear =
      composeSummaryFields.pine_parse_status === "full" && injectReadiness === "ready";
    return !allClear;
  }, [composeSummaryFields.pine_parse_status, injectReadiness, shell]);

  const composeModeMeta = useCallback(
    (id: ComposeOutputMode) => requirements?.compose_modes?.find((m) => m.id === id),
    [requirements?.compose_modes]
  );

  const {
    runGated,
    educationInfoAlerts,
    simTv,
    exitHooks,
    mgmt,
    dontShowAgain,
    setDontShowAgain,
    cancelEducationStep,
    confirmEducationStep,
  } = useStrategyPineEducationGate({
    strategyId: strategy.id,
    fields: composeSummaryFields,
    requirements,
    onShowEducationStep: () => setFlowStep("education"),
  });

  useEffect(() => {
    if (!isOpen) setFlowStep("form");
  }, [isOpen]);

  const handleEducationBack = useCallback(() => {
    cancelEducationStep();
    setFlowStep("form");
  }, [cancelEducationStep]);

  const handleEducationConfirm = useCallback(() => {
    confirmEducationStep();
    setFlowStep("form");
  }, [confirmEducationStep]);

  const educationFooter = useMemo(
    () => <ComposeEducationFooter onBack={handleEducationBack} onConfirm={handleEducationConfirm} />,
    [handleEducationBack, handleEducationConfirm]
  );

  const [policyVersionId, setPolicyVersionId] = useState("");
  const [outputMode, setOutputMode] = useState<ComposeOutputMode>(() =>
    pickDefaultComposeMode(strategy.id, strategyMerged, parseResult)
  );
  const [debugLabels, setDebugLabels] = useState(false);
  const [composing, setComposing] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composeResult, setComposeResult] = useState<ComposeReportStrategyPolicyResponse | null>(null);

  const bundleVersions = useMemo(() => versions.filter(isPolicyBundleVersion), [versions]);

  const policyVersionOptions = useMemo(
    () =>
      versions.map((v) => ({
        value: v.id,
        label: versionOptionLabel(v),
      })),
    [versions]
  );

  useEffect(() => {
    if (!isOpen) return;
    const preferred =
      preferredPolicyVersionId && versions.some((v) => v.id === preferredPolicyVersionId)
        ? preferredPolicyVersionId
        : currentPolicyVersionId && versions.some((v) => v.id === currentPolicyVersionId)
          ? currentPolicyVersionId
          : bundleVersions[0]?.id ?? versions[0]?.id ?? "";
    setPolicyVersionId(preferred);
    setOutputMode(pickDefaultComposeMode(strategy.id, strategyMerged, parseResult));
    setComposeResult(null);
    setComposeError(null);
  }, [
    bundleVersions,
    currentPolicyVersionId,
    isOpen,
    parseResult,
    preferredPolicyVersionId,
    strategy.id,
    strategyMerged,
    versions,
  ]);

  useEffect(() => {
    if (outputMode === "merge_into_strategy" && !mergeAllowed) {
      setOutputMode(
        isIndicatorScript ? "append_policy_to_indicator" : "separate_indicator"
      );
    }
  }, [isIndicatorScript, mergeAllowed, outputMode]);

  const composedText = useMemo(
    () => composeResult?.composed_pine_source ?? composeResult?.pinescript ?? "",
    [composeResult]
  );
  const indicatorText = useMemo(() => composeResult?.indicator_pine_source ?? "", [composeResult]);
  const composeWarnings = useMemo(
    () => normalizeComposeWarnings(composeResult?.warnings),
    [composeResult?.warnings]
  );
  const composeInjectionPreview = requirements?.compose_injection_previews?.[outputMode];
  const composeInjectionWhatHappens = coerceUxCopy(composeInjectionPreview?.what_happens);
  const composeInjectionSite = composeInjectionPreview?.injection_site ?? null;

  const doCompose = useCallback(async () => {
    if (!policyVersionId) {
      toast.warning("Select a policy version");
      return;
    }
    if (outputMode === "merge_into_strategy" && !mergeAllowed) {
      toastWarning(mergeReason ?? "Cannot merge into strategy");
      return;
    }

    setComposing(true);
    setComposeError(null);
    onComposeStart?.();
    if (shell === "pine-detail") {
      onClose();
    }
    try {
      const base = {
        output_mode: outputMode,
        debug_labels: outputMode === "merge_into_strategy" ? debugLabels : false,
        label_style: "descriptive" as const,
      };
      const result = await composeReportStrategyPolicy(strategy.id, {
        ...base,
        policy_version_id: policyVersionId,
      });
      setComposeResult(result);
      storeComposeMode(strategy.id, outputMode);
      try {
        await updateReportStrategy(strategy.id, { default_compose_mode: outputMode });
      } catch {
        // no-op
      }
      if (outputMode === "append_policy_to_indicator") {
        toast.success("Policy appended to indicator", { description: APPEND_POLICY_TO_INDICATOR_USER_COPY });
      } else {
        toast.success("Pinescript composed", {
          description:
            outputMode === "merge_into_strategy"
              ? "Merged policy into your strategy script."
              : "Separate indicator script generated.",
        });
      }
      await onComposeSuccess?.(policyVersionId);
      await onStrategyRefresh();
      if (shell !== "embedded" && shell !== "pine-detail") {
        onClose();
      }
    } catch (e) {
      setComposeResult(null);
      setComposeError(e instanceof Error ? e.message : "Compose failed.");
      onComposeFailed?.();
    } finally {
      setComposing(false);
    }
  }, [
    debugLabels,
    mergeAllowed,
    mergeReason,
    onStrategyRefresh,
    onComposeSuccess,
    onComposeStart,
    onComposeFailed,
    onClose,
    outputMode,
    policyVersionId,
    shell,
    strategy.id,
  ]);

  const onCompose = useCallback(() => {
    runGated(() => doCompose());
  }, [doCompose, runGated]);

  const scriptKindKey =
    strategyMerged.pine_script_kind ?? (isIndicatorScript ? "indicator" : "strategy");
  const scriptKindUx = getPineScriptKindDisplay(scriptKindKey);
  const hasPolicyVersions = versions.length > 0;

  const openGenerateReportWizard = useCallback(() => {
    onClose();
    const params = new URLSearchParams({ strategy_id: strategy.id });
    history.push(`/reports/new?${params.toString()}`);
  }, [history, onClose, strategy.id]);

  const generateReportFooterAction = useMemo(
    () =>
      shell === "pine-detail" || shell === "embedded" ? (
        <ShadcnButton
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          data-strategy-compose-generate-report=""
          className="shrink-0"
          onClick={openGenerateReportWizard}
        >
          <span className="normal-case tracking-normal">Generate a report</span>
        </ShadcnButton>
      ) : (
        <Button
          bgGradient="linear(135deg, #38BBD1, #3EEAEC)"
          color="white"
          _hover={{ opacity: 0.95 }}
          onClick={openGenerateReportWizard}
        >
          Generate a report
        </Button>
      ),
    [openGenerateReportWizard, shell]
  );

  const composePrimaryAction = useMemo(
    () =>
      hasPolicyVersions ? (
    shell === "pine-detail" || shell === "embedded" ? (
      <ShadcnButton
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        data-strategy-compose-submit=""
        className="shrink-0"
        disabled={composing}
        onClick={onCompose}
      >
        {composing ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            <span className="normal-case tracking-normal">Composing…</span>
          </>
        ) : (
          <span className="normal-case tracking-normal">Compose for TradingView</span>
        )}
      </ShadcnButton>
    ) : (
      <Button
        bgGradient="linear(135deg, #38BBD1, #3EEAEC)"
        color="white"
        _hover={{ opacity: 0.95 }}
        onClick={onCompose}
        isLoading={composing}
        loadingText="Composing…"
      >
        Compose for TradingView
      </Button>
    )
  ) : (
    generateReportFooterAction
  ),
    [
      composing,
      generateReportFooterAction,
      hasPolicyVersions,
      onCompose,
      shell,
    ]
  );

  const composeCancelAction = useMemo(() => {
    if (!onCancel || shell === "chakra") return null;
    return (
      <ShadcnButton
        type="button"
        variant="outline-gray"
        size="sm"
        data-font="primary"
        data-strategy-compose-cancel=""
        className="h-8 shrink-0 px-3"
        disabled={composing}
        onClick={onCancel}
      >
        <span className="normal-case tracking-normal">Cancel</span>
      </ShadcnButton>
    );
  }, [composing, onCancel, shell]);

  const composeFormFooter = useMemo(
    () =>
      hasPolicyVersions ? (
        <>
          {composeCancelAction}
          {composePrimaryAction}
        </>
      ) : (
        <>
          {composeCancelAction}
          {generateReportFooterAction}
        </>
      ),
    [composeCancelAction, composePrimaryAction, generateReportFooterAction, hasPolicyVersions]
  );

  useEffect(() => {
    if (shell !== "embedded" || !onComposeFooterChange) return;
    onComposeFooterChange(flowStep === "form" ? composeFormFooter : null);
    return () => onComposeFooterChange(null);
  }, [composeFormFooter, flowStep, onComposeFooterChange, shell]);

  useEffect(() => {
    if (shell !== "embedded" || !onEmbeddedPresentationChange) return;
    if (flowStep === "education") {
      onEmbeddedPresentationChange({
        title: COMPOSE_EDUCATION_STEP_TITLE,
        description: COMPOSE_EDUCATION_STEP_DESCRIPTION,
        footer: educationFooter,
        size: "compact",
      });
    } else {
      onEmbeddedPresentationChange({
        title: "Compose for TradingView",
        description: COMPOSE_DIALOG_DESCRIPTION,
        footer: composeFormFooter,
        size: "compact",
      });
    }
    return () => onEmbeddedPresentationChange(null);
  }, [
    composeFormFooter,
    educationFooter,
    flowStep,
    onEmbeddedPresentationChange,
    shell,
  ]);

  const composeFormBody = (
    <VStack align="stretch" spacing={4} className="min-w-0">
      {shell === "chakra" ? (
        <Text fontSize="sm" color={subtleText}>
          {COMPOSE_DIALOG_DESCRIPTION} Active output appears in Active TradingView Pinescript on the strategy
          page.
        </Text>
      ) : null}

      {!hasPolicyVersions ? (
              <StrategyComposeNoPolicyPrompt
                strategyId={strategy.id}
                subtleText={subtleText}
                textColor={textColor}
                onNavigateAway={onClose}
              />
            ) : (
              <>
            {educationInfoAlerts}

            {showReadinessSummary ? (
              <StrategyPineParseSummaryAlert
                fields={composeSummaryFields}
                issues={parseIssues}
                parseStatusUx={requirements?.parse_status_ux}
                injectReadinessUx={requirements?.inject_readiness_ux}
                exitHookReadinessUx={requirements?.exit_hook_readiness_ux}
              />
            ) : null}

            {isIndicatorScript && scriptKindUx ? (
              <Alert status="info" borderRadius="8px" fontSize="sm">
                <AlertIcon />
                <AlertDescription>{scriptKindUx.description}</AlertDescription>
              </Alert>
            ) : null}

            <StrategyPineExecutionModelCallout
              executionModel={executionModel}
              subtleText={subtleText}
              textColor={textColor}
              borderColor={borderColor}
            />

            {parseIssues.length > 0 ? (
              <StrategyPineParseIssues
                issues={parseIssues}
                subtleText={subtleText}
                textColor={textColor}
                borderColor={borderColor}
                grouped
              />
            ) : null}

            <Field data-strategy-compose-policy-version="">
              <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Policy version</FieldLabel>
              <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                Choose the saved policy version or combined bundle to attach when composing your
                TradingView export.
              </FieldDescription>
              <div
                className="w-full min-w-0"
                data-strategy-compose-policy-version-select=""
              >
                <TradeFilterSelectFullWidth
                  value={policyVersionId}
                  onValueChange={setPolicyVersionId}
                  options={policyVersionOptions}
                  placeholder="Select a policy version"
                  emptyValueShowsPlaceholder
                />
              </div>
              <FieldDescription className={cn(STRATEGY_PINE_SECTION_DESC_CLASS, "mt-2")}>
                Need entry + management together? Combine versions on the{" "}
                {onNavigateToPoliciesTab ? (
                  <button
                    type="button"
                    className="underline underline-offset-4 hover:text-primary"
                    data-strategy-compose-policies-tab-link=""
                    onClick={() => onNavigateToPoliciesTab()}
                  >
                    Policies
                  </button>
                ) : (
                  "Policies"
                )}{" "}
                tab, then select the saved bundle here.
              </FieldDescription>
            </Field>

            <StrategyComposeModePicker
              isIndicatorScript={isIndicatorScript}
              mergeAllowed={mergeAllowed}
              mergeReason={mergeReason}
              outputMode={outputMode}
              onOutputModeChange={setOutputMode}
              showContextBadges={shell !== "embedded"}
              mergeModeLabel={
                coerceUxCopy(composeModeMeta("merge_into_strategy")?.label) ??
                "Merge into my strategy"
              }
              mergeModeDescription={
                coerceUxCopy(composeModeMeta("merge_into_strategy")?.description) ??
                "One script; Tessera gates when your script opens trades."
              }
              appendModeLabel={
                coerceUxCopy(composeModeMeta("append_policy_to_indicator")?.label) ??
                "Append policy to indicator"
              }
              appendModeDescription={
                coerceUxCopy(composeModeMeta("append_policy_to_indicator")?.description) ??
                "Policy block appended at end; plots unchanged."
              }
              separateModeLabel={
                coerceUxCopy(composeModeMeta("separate_indicator")?.label) ?? "Separate indicator"
              }
              separateModeDescription={
                coerceUxCopy(composeModeMeta("separate_indicator")?.description) ??
                "Two scripts on the chart."
              }
              parseIssues={parseIssues}
              scriptKind={scriptKindKey}
              injectReadiness={injectReadiness}
            />

            {composeInjectionPreview ? (
              <StrategyComposeInjectionPreviewCard
                preview={composeInjectionPreview}
                whatHappens={composeInjectionWhatHappens}
                injectionSite={composeInjectionSite}
                injectionSiteUx={requirements?.injection_site_ux}
              />
            ) : null}

            {outputMode === "merge_into_strategy" && mergeAllowed ? (
              <Field data-strategy-compose-debug-labels="" className="w-full min-w-0">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="strategy-compose-debug-labels"
                    checked={debugLabels}
                    onCheckedChange={(checked) => setDebugLabels(checked === true)}
                    className="mt-0.5"
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <FieldLabel
                      htmlFor="strategy-compose-debug-labels"
                      className={cn(
                        STRATEGY_PINE_SECTION_LABEL_CLASS,
                        "cursor-pointer font-normal"
                      )}
                    >
                      Show policy hints on chart
                    </FieldLabel>
                    <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                      Optional. Adds small labels inside the Tessera block on your chart for
                      rules TradingView cannot run in Pine, including breakeven, trailing stops,
                      time stops, and blocked filters. Your entry logic is unchanged.
                    </FieldDescription>
                  </div>
                </div>
              </Field>
            ) : null}

            {composeError ? (
              <Alert status="error" borderRadius="8px" fontSize="sm">
                <AlertIcon />
                <AlertDescription>{composeError}</AlertDescription>
              </Alert>
            ) : null}

            {composeWarnings.length > 0 ? (
              <StrategyPineParseIssues
                issues={composeWarnings}
                subtleText={subtleText}
                textColor={textColor}
                borderColor={borderColor}
                grouped
              />
            ) : null}

            {composedText && outputMode === "separate_indicator" ? (
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                  Your strategy (snapshot at compose)
                </Text>
                <PineScriptCodeBlock code={composedText} maxH="240px" />
              </Box>
            ) : null}

            {composedText &&
            (outputMode === "merge_into_strategy" || outputMode === "append_policy_to_indicator") ? (
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                  {formatComposeModeLabel(outputMode)} preview
                </Text>
                <PineScriptCodeBlock code={composedText} maxH="280px" />
              </Box>
            ) : null}

            {indicatorText && outputMode === "separate_indicator" ? (
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                  Tessera indicator overlay preview
                </Text>
                <PineScriptCodeBlock code={indicatorText} maxH="240px" />
              </Box>
            ) : null}
              </>
            )}
    </VStack>
  );

  const educationStepBody = (
    <StrategyPineComposeEducationStep
      simTv={simTv}
      exitHooks={exitHooks}
      mgmt={mgmt}
      dontShowAgain={dontShowAgain}
      onDontShowAgainChange={setDontShowAgain}
    />
  );

  const activeBody = flowStep === "education" ? educationStepBody : composeFormBody;
  const activeTitle =
    flowStep === "education" ? COMPOSE_EDUCATION_STEP_TITLE : "Compose for TradingView";
  const activeDescription =
    flowStep === "education" ? COMPOSE_EDUCATION_STEP_DESCRIPTION : COMPOSE_DIALOG_DESCRIPTION;
  const activeFooter = flowStep === "education" ? educationFooter : composeFormFooter;

  if (shell === "embedded") {
    return (
      <Box data-strategy-compose-dialog="" className="min-w-0">
        {activeBody}
      </Box>
    );
  }

  if (shell === "pine-detail") {
    return (
      <StrategyPineDetailDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) (onCancel ?? onClose)();
        }}
        size="compact"
        contentClassName={STRATEGY_COMPOSE_DIALOG_CONTENT_CLASS}
        contentProps={{ "data-strategy-compose-dialog": "" }}
        title={activeTitle}
        description={activeDescription}
        footer={activeFooter}
        dismissLabel="Cancel"
        showDismissButton={false}
      >
        <Box data-strategy-compose-dialog="" className="min-w-0">
          {activeBody}
        </Box>
      </StrategyPineDetailDialog>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="640px">
        <ModalHeader fontFamily="'Urbanist', sans-serif">{activeTitle}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {flowStep === "education" ? (
            <Text fontSize="sm" color={subtleText} mb={4}>
              {COMPOSE_EDUCATION_STEP_DESCRIPTION}
            </Text>
          ) : null}
          {activeBody}
        </ModalBody>
        <ModalFooter>
          {flowStep === "education" ? (
            educationFooter
          ) : (
            <>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Close
              </Button>
              {composePrimaryAction}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
